import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import isNumber from 'lodash/isNumber.js';
import CalendarPane from './calendar-pane.vue.js';
import Attribute from './utils/attribute.js';
import { pageForDate, pageIsBetweenPages, addPages, pageIsValid, pageForThisMonth, pageIsBeforePage, pageIsAfterPage } from './utils/helpers.js';

var script = {
  name: 'Calendar',
  components: {
    CalendarPane
  },
  inject: ['state'],
  props: {
    activeAttribute: Object,
    disabledAttribute: Object
  },
  data() {
    return {
      pages: [],
      transitionName: '',
      isTransitioning: false,
      attribute: null,
      pinAttr: null
    };
  },
  computed: {
    locale() {
      return this.state.locale;
    },
    minPage() {
      return pageForDate(this.locale.toDate(this.state.minDate));
    },
    maxPage() {
      return pageForDate(this.locale.toDate(this.state.maxDate));
    },
    count() {
      return this.state.visibleColumnCount;
    }
  },
  watch: {
    count(count) {
      if (count > 1) {
        this.movePrev();
      } else if (this.state.model) {
        this.move(this.getPageForAttributes());
      } else {
        this.moveNext();
      }
    },
    activeAttribute() {
      this.refreshActiveAttribute();
      this.refreshAttributes();
    },
    pages() {
      this.refreshAttributes();
    },
    disabledAttribute() {
      this.refreshDisabledDays(this.pages);
    }
  },
  created() {
    this.refreshActiveAttribute();
    this.refreshAttributes();
    this.refreshPages();
  },
  methods: {
    refreshActiveAttribute() {
      if (!this.activeAttribute || !this.activeAttribute.dates) {
        this.attribute = null;
        this.pinAttr = null;
        return;
      }
      let pinAttr = null;
      const attribute = new Attribute(this.activeAttribute, this.locale);
      if (attribute && attribute.pinPage) {
        pinAttr = attribute;
      }
      this.attribute = attribute;
      this.pinAttr = pinAttr;
    },
    canMove(page) {
      return pageIsBetweenPages(page, this.minPage, this.maxPage);
    },
    movePrev() {
      this.move(-this.state.step);
    },
    moveNext() {
      this.move(this.state.step);
    },
    move(monthsOrPage) {
      const page = isNumber(monthsOrPage) ? addPages(this.pages[0], monthsOrPage) : monthsOrPage;
      this.refreshPages({
        page,
        position: 1
      });
    },
    refreshPages() {
      let {
        page,
        position = 1
      } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      let fromPage = null;
      if (pageIsValid(page)) {
        const pagesToAdd = position > 0 ? 1 - position : -(this.count + position);
        fromPage = addPages(page, pagesToAdd);
      } else {
        fromPage = this.getPageForAttributes();
      }
      fromPage = pageIsValid(fromPage) ? fromPage : pageForThisMonth();
      const toPage = addPages(fromPage, this.count - 1);
      if (pageIsBeforePage(fromPage, this.minPage)) {
        fromPage = this.minPage;
      } else if (pageIsAfterPage(toPage, this.maxPage)) {
        fromPage = addPages(this.maxPage, 1 - this.count);
      }
      const pages = [...Array(this.count).keys()].map(i => this.buildPage(addPages(fromPage, i), i));
      this.refreshDisabledDays(pages);
      this.transitionName = this.getPageTransition(this.pages[0], pages[0]);
      this.pages = pages;
      this.$emit('update:from-page', fromPage);
      this.$emit('update:to-page', toPage);
    },
    refreshDisabledDays(pages) {
      pages.reduce((prev, curr) => prev.concat(curr.days), []).forEach(day => {
        day.disabled = !!this.disabledAttribute && this.disabledAttribute.includesDay(day);
      });
    },
    getPageTransition(oldPage, newPage) {
      if (this.count > 1 || !pageIsValid(oldPage) || !pageIsValid(newPage)) {
        return 'fade';
      }
      const movePrev = pageIsBeforePage(newPage, oldPage);
      return movePrev ? 'slide-right' : 'slide-left';
    },
    getPageForAttributes() {
      const attr = this.pinAttr;
      if (attr && attr.hasDates) {
        let [date] = attr.dates;
        date = date.start || date.date;
        return pageForDate(this.locale.toDate(date));
      }
      return null;
    },
    buildPage(_ref, position) {
      let {
        month,
        year
      } = _ref;
      const key = `${year.toString()}-${month.toString()}`;
      let page = this.pages.find(pg => pg.key === key);
      if (!page) {
        const date = new Date(year, month - 1, 15);
        const monthComps = this.locale.getMonthComps(month, year);
        const prevMonthComps = this.locale.getPrevMonthComps(month, year);
        const nextMonthComps = this.locale.getNextMonthComps(month, year);
        page = {
          key,
          month,
          year,
          title: this.locale.format(date, this.locale.masks.title),
          shortMonthLabel: this.locale.format(date, 'MMM'),
          monthLabel: this.locale.format(date, 'MMMM'),
          shortYearLabel: year.toString().substring(2),
          yearLabel: year.toString(),
          monthComps,
          prevMonthComps,
          nextMonthComps,
          canMove: pg => this.canMove(pg),
          move: pg => this.move(pg),
          movePrevMonth: () => this.move(prevMonthComps),
          moveNextMonth: () => this.move(nextMonthComps)
        };
        page.days = this.locale.getCalendarDays(page);
      }
      page.position = position;
      return page;
    },
    refreshAttributes() {
      if (this.pages.length === 0) {
        return;
      }
      this.pages.forEach(page => {
        page.days.forEach(day => {
          if (!this.attribute || !this.attribute.dates) {
            day.attribute = null;
            return;
          }
          const targetDate = this.attribute.includesDay(day);
          if (targetDate) {
            day.attribute = _objectSpread2(_objectSpread2({}, this.attribute), {}, {
              targetDate
            });
          } else {
            day.attribute = null;
          }
        });
      });
    }
  }
};

export { script as default };
//# sourceMappingURL=calendar.vue2.js.map
